import { getSupabaseBrowserClient } from '../lib/supabase'

export type P4Objective = {
  id: string
  title: string
  description: string | null
  intendedOutcome: string | null
  successMeasure: string | null
  displayOrder: number
  status: 'active' | 'archived'
  initiativeCount: number
  useCaseCount: number
  carriedForward: boolean
  priorityIds: string[]
  measures: P4Measure[]
}

export type P4MeasureUpdate = {
  id: string
  recordedPosition: string
  recordedAt: string
  sourceNote: string | null
}

export type P4Measure = {
  id: string
  code: string
  title: string
  target: string | null
  displayOrder: number
  status: 'active' | 'archived'
  latestUpdate: P4MeasureUpdate | null
}

export type P4Priority = {
  id: string
  code: string
  title: string
  description: string | null
  displayOrder: number
  carriedForward: boolean
}

export type P4FocusZone = 'focus_now' | 'explore' | 'not_current_priority'

export type P4FocusArea = {
  id: string
  capabilityCode: string
  label: string
  zone: P4FocusZone
  displayOrder: number
  priorityIds: string[]
}

export type P4Principle = {
  id: string
  code: string
  title: string
  statement: string
  source: 'swell_recommended' | 'customer'
  displayOrder: number
  carriedForward: boolean
}

export type P4PortfolioLevel = 'low' | 'medium' | 'high'
export type P4RoadmapHorizon = 'now' | 'next' | 'later'

export type P4InitiativePosition = {
  id: string
  initiativeId: string
  expectedValue: P4PortfolioLevel
  readiness: P4PortfolioLevel
  roadmapHorizon: P4RoadmapHorizon
  placementSource: 'suggested' | 'confirmed'
  valueBasis: string[]
  readinessBasis: string[]
  carriedForward: boolean
}

export type P4StrategyVersion = {
  id: string
  versionNo: number
  name: string
  periodStart: string | null
  periodEnd: string | null
  directionStatement: string
  executiveSponsorMembershipId: string | null
  executiveSponsorName: string | null
  status: 'draft' | 'current' | 'superseded'
  madeCurrentAt: string | null
  createdAt: string
  copiedFromStrategyVersionId: string | null
  priorities: P4Priority[]
  focusAreas: P4FocusArea[]
  principles: P4Principle[]
  initiativePositions: P4InitiativePosition[]
  objectives: P4Objective[]
}

export type P4Strategy = { versions: P4StrategyVersion[] }

export type OrganisationStrategyTypeCode =
  | 'productivity'
  | 'customer_experience'
  | 'growth_new_value'
  | 'better_decisions'
  | 'resilience'
  | 'workforce_capability'
  | 'other_mixed'

export type OrganisationStrategyPriority = {
  id: string
  title: string
  description: string | null
  displayOrder: number
  status: 'active' | 'inactive'
  strategyTypeCode: OrganisationStrategyTypeCode
  aiRelevance: 'high' | 'medium' | 'low' | 'unknown'
}

export type OrganisationStrategyContext = {
  version: {
    id: string
    versionNo: number
    name: string
    source: 'customer_existing' | 'swell_established'
    effectiveFrom: string | null
    madeCurrentAt: string | null
  } | null
  priorities: OrganisationStrategyPriority[]
  links: { aiPriorityId: string; organisationPriorityId: string; relationshipStatus: 'suggested' | 'confirmed' }[]
}


export type P4InitiativeStage =
  | 'idea'
  | 'under_consideration'
  | 'prioritised'
  | 'planned'
  | 'in_progress'
  | 'on_hold'
  | 'delivered'
  | 'cancelled'
  | 'archived'

export type P4Initiative = {
  id: string
  name: string
  description: string | null
  ownerMembershipId: string | null
  ownerName: string | null
  stage: P4InitiativeStage
  targetStartDate: string | null
  targetEndDate: string | null
  targetPeriodLabel: string | null
  expectedOutcome: string | null
  createdAt: string
  objectives: { id: string; title: string }[]
  useCases: { id: string; title: string }[]
}

export type P4Product = { id: string; name: string; supplierId: string | null; supplierName: string | null }

export type P4UseCase = {
  id: string
  title: string
  businessPurpose: string
  expectedOutcome: string | null
  businessUnitId: string | null
  businessUnitName: string | null
  businessOwnerMembershipId: string | null
  businessOwnerName: string | null
  technicalOwnerMembershipId: string | null
  technicalOwnerName: string | null
  operationalState: 'proposed' | 'experiment' | 'pilot' | 'production' | 'suspended' | 'retired'
  governanceStatus: string
  source: string
  createdAt: string
  products: { id: string; name: string; supplierName: string | null }[]
  objectives: { id: string; title: string }[]
  initiatives: { id: string; name: string; stage?: string }[]
}

export type P4UseCaseDetail = P4UseCase & {
  organisationId: string
  updatedAt: string
  candidateSourceId: string | null
  activity: { id: string; eventType: string; occurredAt: string; eventData: Record<string, unknown> }[]
}

type RpcError = { message: string }
type RpcResponse = PromiseLike<{ data: unknown; error: RpcError | null }>
type RpcInvoker = (functionName: string, args?: Record<string, unknown>) => RpcResponse

async function invoke(functionName: string, args: Record<string, unknown> = {}) {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const { data, error } = await client.rpc(functionName, args)
  if (error) throw new Error(error.message)
  return data
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
function records(value: unknown): Record<string, unknown>[] { return Array.isArray(value) ? value.filter(isRecord) : [] }
function stringValue(value: unknown, fallback = ''): string { return typeof value === 'string' ? value : fallback }
function nullableString(value: unknown): string | null { return typeof value === 'string' ? value : null }
function numberValue(value: unknown): number { if (typeof value === 'number') return value; if (typeof value === 'string') return Number(value) || 0; return 0 }
function booleanValue(value: unknown): boolean { return value === true }
function stringArray(value: unknown): string[] { return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [] }
function numberArray(value: unknown): number[] { return Array.isArray(value) ? value.map(numberValue).filter((item) => Number.isFinite(item)) : [] }

function parseMeasure(row: Record<string, unknown>): P4Measure {
  const latest = isRecord(row.latestUpdate) ? row.latestUpdate : null
  return {
    id: stringValue(row.id),
    code: stringValue(row.code),
    title: stringValue(row.title),
    target: nullableString(row.target),
    displayOrder: numberValue(row.displayOrder),
    status: stringValue(row.status, 'active') as P4Measure['status'],
    latestUpdate: latest ? {
      id: stringValue(latest.id),
      recordedPosition: stringValue(latest.recordedPosition),
      recordedAt: stringValue(latest.recordedAt),
      sourceNote: nullableString(latest.sourceNote),
    } : null,
  }
}

function parseObjective(row: Record<string, unknown>): P4Objective {
  return {
    id: stringValue(row.id),
    title: stringValue(row.title),
    description: nullableString(row.description),
    intendedOutcome: nullableString(row.intendedOutcome),
    successMeasure: nullableString(row.successMeasure),
    displayOrder: numberValue(row.displayOrder),
    status: stringValue(row.status, 'active') as P4Objective['status'],
    initiativeCount: numberValue(row.initiativeCount),
    useCaseCount: numberValue(row.useCaseCount),
    carriedForward: booleanValue(row.carriedForward),
    priorityIds: stringArray(row.priorityIds),
    measures: records(row.measures).map(parseMeasure),
  }
}

export async function getP4Strategy(organisationId: string): Promise<P4Strategy> {
  const data = await invoke('get_p4_strategy', { p_organisation_id: organisationId })
  if (!isRecord(data)) return { versions: [] }
  return {
    versions: records(data.versions).map((row) => ({
      id: stringValue(row.id),
      versionNo: numberValue(row.versionNo),
      name: stringValue(row.name),
      periodStart: nullableString(row.periodStart),
      periodEnd: nullableString(row.periodEnd),
      directionStatement: stringValue(row.directionStatement),
      executiveSponsorMembershipId: nullableString(row.executiveSponsorMembershipId),
      executiveSponsorName: nullableString(row.executiveSponsorName),
      status: stringValue(row.status, 'draft') as P4StrategyVersion['status'],
      madeCurrentAt: nullableString(row.madeCurrentAt),
      createdAt: stringValue(row.createdAt),
      copiedFromStrategyVersionId: nullableString(row.copiedFromStrategyVersionId),
      priorities: records(row.priorities).map((item) => ({
        id: stringValue(item.id),
        code: stringValue(item.code),
        title: stringValue(item.title),
        description: nullableString(item.description),
        displayOrder: numberValue(item.displayOrder),
        carriedForward: booleanValue(item.carriedForward),
      })),
      focusAreas: records(row.focusAreas).map((item) => ({
        id: stringValue(item.id),
        capabilityCode: stringValue(item.capabilityCode),
        label: stringValue(item.label),
        zone: stringValue(item.zone, 'explore') as P4FocusArea['zone'],
        displayOrder: numberValue(item.displayOrder),
        priorityIds: stringArray(item.priorityIds),
      })),
      principles: records(row.principles).map((item) => ({
        id: stringValue(item.id),
        code: stringValue(item.code),
        title: stringValue(item.title),
        statement: stringValue(item.statement),
        source: stringValue(item.source, 'swell_recommended') as P4Principle['source'],
        displayOrder: numberValue(item.displayOrder),
        carriedForward: booleanValue(item.carriedForward),
      })),
      initiativePositions: records(row.initiativePositions).map((item) => ({
        id: stringValue(item.id),
        initiativeId: stringValue(item.initiativeId),
        expectedValue: stringValue(item.expectedValue, 'medium') as P4PortfolioLevel,
        readiness: stringValue(item.readiness, 'medium') as P4PortfolioLevel,
        roadmapHorizon: stringValue(item.roadmapHorizon, 'later') as P4RoadmapHorizon,
        placementSource: stringValue(item.placementSource, 'suggested') as P4InitiativePosition['placementSource'],
        valueBasis: stringArray(item.valueBasis),
        readinessBasis: stringArray(item.readinessBasis),
        carriedForward: booleanValue(item.carriedForward),
      })),
      objectives: records(row.objectives).map(parseObjective),
    })),
  }
}

export async function getOrganisationStrategyContext(organisationId: string, aiStrategyVersionId?: string | null): Promise<OrganisationStrategyContext> {
  const row = await invoke('get_organisation_strategy_context', {
    p_organisation_id: organisationId,
    p_ai_strategy_version_id: aiStrategyVersionId || null,
  })
  const data = isRecord(row) ? row : {}
  const version = isRecord(data.version) ? data.version : null
  return {
    version: version ? {
      id: stringValue(version.id),
      versionNo: numberValue(version.versionNo),
      name: stringValue(version.name),
      source: stringValue(version.source, 'customer_existing') as 'customer_existing' | 'swell_established',
      effectiveFrom: nullableString(version.effectiveFrom),
      madeCurrentAt: nullableString(version.madeCurrentAt),
    } : null,
    priorities: records(data.priorities).map((item) => ({
      id: stringValue(item.id),
      title: stringValue(item.title),
      description: nullableString(item.description),
      displayOrder: numberValue(item.displayOrder),
      status: stringValue(item.status, 'active') as OrganisationStrategyPriority['status'],
      strategyTypeCode: stringValue(item.strategyTypeCode, 'other_mixed') as OrganisationStrategyTypeCode,
      aiRelevance: stringValue(item.aiRelevance, 'unknown') as OrganisationStrategyPriority['aiRelevance'],
    })),
    links: records(data.links).map((item) => ({
      aiPriorityId: stringValue(item.aiPriorityId),
      organisationPriorityId: stringValue(item.organisationPriorityId),
      relationshipStatus: stringValue(item.relationshipStatus, 'confirmed') as 'suggested' | 'confirmed',
    })),
  }
}

export async function saveOrganisationStrategyPriorities(input: {
  organisationId: string
  name: string
  priorities: Omit<OrganisationStrategyPriority, 'id'>[]
}) {
  return stringValue(await invoke('save_organisation_strategy_priorities', {
    p_organisation_id: input.organisationId,
    p_name: input.name,
    p_priorities: input.priorities,
  }))
}

export async function saveAiStrategyOrganisationPriorityLinks(input: {
  strategyVersionId: string
  links: { aiPriorityId: string; organisationPriorityId: string; relationshipStatus?: 'suggested' | 'confirmed' }[]
}) {
  await invoke('save_ai_strategy_organisation_priority_links', {
    p_ai_strategy_version_id: input.strategyVersionId,
    p_links: input.links,
  })
}

export async function createP4StrategyVersion(input: {
  organisationId: string
  name: string
  directionStatement: string
  periodStart?: string | null
  periodEnd?: string | null
  executiveSponsorMembershipId?: string | null
}) {
  return stringValue(await invoke('create_p4_strategy_version', {
    p_organisation_id: input.organisationId,
    p_name: input.name,
    p_direction_statement: input.directionStatement,
    p_period_start: input.periodStart || null,
    p_period_end: input.periodEnd || null,
    p_executive_sponsor_membership_id: input.executiveSponsorMembershipId || null,
  }))
}

export async function updateP4StrategyVersion(input: Omit<Parameters<typeof createP4StrategyVersion>[0], 'organisationId'> & { strategyVersionId: string }) {
  await invoke('update_p4_strategy_version', {
    p_strategy_version_id: input.strategyVersionId,
    p_name: input.name,
    p_direction_statement: input.directionStatement,
    p_period_start: input.periodStart || null,
    p_period_end: input.periodEnd || null,
    p_executive_sponsor_membership_id: input.executiveSponsorMembershipId || null,
  })
}

export async function makeP4StrategyCurrent(strategyVersionId: string) {
  await invoke('make_p4_strategy_current', { p_strategy_version_id: strategyVersionId })
}

export async function createP4Objective(input: {
  strategyVersionId: string
  title: string
  description?: string | null
  intendedOutcome?: string | null
  successMeasure?: string | null
  target?: string | null
}) {
  return stringValue(await invoke('create_p4_objective', {
    p_strategy_version_id: input.strategyVersionId,
    p_title: input.title,
    p_description: input.description || null,
    p_intended_outcome: input.intendedOutcome || null,
    p_success_measure: input.successMeasure || null,
  }))
}

export async function updateP4Objective(input: {
  objectiveId: string
  title: string
  description?: string | null
  intendedOutcome?: string | null
  successMeasure?: string | null
}) {
  await invoke('update_p4_objective', {
    p_objective_id: input.objectiveId,
    p_title: input.title,
    p_description: input.description || null,
    p_intended_outcome: input.intendedOutcome || null,
    p_success_measure: input.successMeasure || null,
  })
}

export async function archiveP4Objective(objectiveId: string) {
  await invoke('archive_p4_objective', { p_objective_id: objectiveId })
}

export async function listP4Initiatives(organisationId: string): Promise<P4Initiative[]> {
  const data = await invoke('list_p4_initiatives', { p_organisation_id: organisationId })
  return records(data).map((row) => ({
    id: stringValue(row.id),
    name: stringValue(row.name),
    description: nullableString(row.description),
    ownerMembershipId: nullableString(row.ownerMembershipId),
    ownerName: nullableString(row.ownerName),
    stage: stringValue(row.stage, 'idea') as P4InitiativeStage,
    targetStartDate: nullableString(row.targetStartDate),
    targetEndDate: nullableString(row.targetEndDate),
    targetPeriodLabel: nullableString(row.targetPeriodLabel),
    expectedOutcome: nullableString(row.expectedOutcome),
    createdAt: stringValue(row.createdAt),
    objectives: records(row.objectives).map((item) => ({ id: stringValue(item.id), title: stringValue(item.title) })),
    useCases: records(row.useCases).map((item) => ({ id: stringValue(item.id), title: stringValue(item.title) })),
  }))
}

export async function createP4Initiative(input: {
  organisationId: string
  name: string
  description?: string | null
  ownerMembershipId?: string | null
  stage: P4InitiativeStage
  targetStartDate?: string | null
  targetEndDate?: string | null
  targetPeriodLabel?: string | null
  expectedOutcome?: string | null
}) {
  return stringValue(await invoke('create_p4_initiative', {
    p_organisation_id: input.organisationId,
    p_name: input.name,
    p_description: input.description || null,
    p_owner_membership_id: input.ownerMembershipId || null,
    p_stage: input.stage,
    p_target_start_date: input.targetStartDate || null,
    p_target_end_date: input.targetEndDate || null,
    p_target_period_label: input.targetPeriodLabel || null,
    p_expected_outcome: input.expectedOutcome || null,
  }))
}

export async function createDec039PrelinkedInitiative(input: {
  organisationId: string
  contributionId: string
  name: string
  expectedOutcome?: string | null
}) {
  return stringValue(await invoke('create_dec039_prelinked_initiative', {
    p_organisation_id: input.organisationId,
    p_contribution_id: input.contributionId,
    p_name: input.name,
    p_expected_outcome: input.expectedOutcome || null,
  }))
}

export async function updateP4Initiative(input: Omit<Parameters<typeof createP4Initiative>[0], 'organisationId'> & { initiativeId: string }) {
  await invoke('update_p4_initiative', {
    p_initiative_id: input.initiativeId,
    p_name: input.name,
    p_description: input.description || null,
    p_owner_membership_id: input.ownerMembershipId || null,
    p_stage: input.stage,
    p_target_start_date: input.targetStartDate || null,
    p_target_end_date: input.targetEndDate || null,
    p_target_period_label: input.targetPeriodLabel || null,
    p_expected_outcome: input.expectedOutcome || null,
  })
}

export async function setP4InitiativeObjectives(initiativeId: string, objectiveIds: string[]) {
  await invoke('set_p4_initiative_objectives', { p_initiative_id: initiativeId, p_objective_ids: objectiveIds })
}

export async function setP4InitiativeUseCases(initiativeId: string, useCaseIds: string[]) {
  await invoke('set_p4_initiative_use_cases', { p_initiative_id: initiativeId, p_use_case_ids: useCaseIds })
}

export type StrategyStudioCatalogue = {
  capabilities: { id: string; code: string; label: string; displayOrder: number }[]
  snapshot: {
    aiUseCount: number
    needsAttentionCount: number
    unalignedCount: number
    capabilityActivity: { code: string; label: string; count: number }[]
    expectedBenefits: { code: string; label: string; count: number }[]
  }
}

export async function getStrategyStudioCatalogue(organisationId: string): Promise<StrategyStudioCatalogue> {
  const data = await invoke('get_strategy_studio_catalogue', { p_organisation_id: organisationId })
  if (!isRecord(data)) throw new Error('Strategy Studio catalogue response was invalid.')
  const snapshot = isRecord(data.snapshot) ? data.snapshot : {}
  return {
    capabilities: records(data.capabilities).map((item) => ({
      id: stringValue(item.id),
      code: stringValue(item.code),
      label: stringValue(item.label),
      displayOrder: numberValue(item.displayOrder),
    })),
    snapshot: {
      aiUseCount: numberValue(snapshot.aiUseCount),
      needsAttentionCount: numberValue(snapshot.needsAttentionCount),
      unalignedCount: numberValue(snapshot.unalignedCount),
      capabilityActivity: records(snapshot.capabilityActivity).map((item) => ({
        code: stringValue(item.code),
        label: stringValue(item.label),
        count: numberValue(item.count),
      })),
      expectedBenefits: records(snapshot.expectedBenefits).map((item) => ({
        code: stringValue(item.code),
        label: stringValue(item.label),
        count: numberValue(item.count),
      })),
    },
  }
}

export async function saveStrategyStudioFoundation(input: {
  strategyVersionId: string
  priorities: P4Priority[]
  focusAreas: P4FocusArea[]
  principles: P4Principle[]
  objectivePriorityLinks: { objectiveId: string; priorityId: string; relationshipStatus: 'suggested' | 'confirmed' }[]
}) {
  await invoke('save_strategy_studio_foundation', {
    p_strategy_version_id: input.strategyVersionId,
    p_priorities: input.priorities,
    p_focus_areas: input.focusAreas,
    p_principles: input.principles,
    p_objective_priority_links: input.objectivePriorityLinks,
  })
}

export async function saveStrategyObjectiveMeasures(objectiveId: string, measures: P4Measure[]) {
  await invoke('save_strategy_objective_measures', {
    p_strategic_objective_id: objectiveId,
    p_measures: measures,
  })
}

export async function recordStrategyMeasureUpdate(input: {
  measureId: string
  recordedPosition: string
  recordedAt: string
  sourceNote?: string | null
}) {
  return stringValue(await invoke('record_strategy_measure_update', {
    p_measure_id: input.measureId,
    p_recorded_position: input.recordedPosition,
    p_recorded_at: input.recordedAt,
    p_source_note: input.sourceNote || null,
  }))
}

export async function setStrategyInitiativePosition(input: {
  strategyVersionId: string
  initiativeId: string
  expectedValue: P4PortfolioLevel
  readiness: P4PortfolioLevel
  roadmapHorizon: P4RoadmapHorizon
  placementSource: 'suggested' | 'confirmed'
  valueBasis: string[]
  readinessBasis: string[]
}) {
  await invoke('set_strategy_initiative_position', {
    p_strategy_version_id: input.strategyVersionId,
    p_initiative_id: input.initiativeId,
    p_expected_value: input.expectedValue,
    p_readiness: input.readiness,
    p_roadmap_horizon: input.roadmapHorizon,
    p_placement_source: input.placementSource,
    p_value_basis: input.valueBasis,
    p_readiness_basis: input.readinessBasis,
  })
}

export async function listMyP4Initiatives(organisationId: string): Promise<P4Initiative[]> {
  const data = await invoke('list_my_p4_initiatives', { p_organisation_id: organisationId })
  return records(data).map((row) => ({
    id: stringValue(row.id),
    name: stringValue(row.name),
    description: nullableString(row.description),
    ownerMembershipId: nullableString(row.ownerMembershipId),
    ownerName: null,
    stage: stringValue(row.stage, 'idea') as P4InitiativeStage,
    targetStartDate: nullableString(row.targetStartDate),
    targetEndDate: nullableString(row.targetEndDate),
    targetPeriodLabel: nullableString(row.targetPeriodLabel),
    expectedOutcome: nullableString(row.expectedOutcome),
    createdAt: stringValue(row.createdAt),
    objectives: [],
    useCases: [],
  }))
}

export async function updateP4InitiativeFacts(input: {
  initiativeId: string
  name: string
  description?: string | null
  targetStartDate?: string | null
  targetEndDate?: string | null
  targetPeriodLabel?: string | null
  expectedOutcome?: string | null
}) {
  await invoke('update_p4_initiative_facts', {
    p_initiative_id: input.initiativeId,
    p_name: input.name,
    p_description: input.description || null,
    p_target_start_date: input.targetStartDate || null,
    p_target_end_date: input.targetEndDate || null,
    p_target_period_label: input.targetPeriodLabel || null,
    p_expected_outcome: input.expectedOutcome || null,
  })
}

export async function searchP4Products(organisationId: string, query: string): Promise<P4Product[]> {
  const data = await invoke('search_p4_products', { p_organisation_id: organisationId, p_query: query })
  return records(data).map((row) => ({
    id: stringValue(row.id),
    name: stringValue(row.name),
    supplierId: nullableString(row.supplierId),
    supplierName: nullableString(row.supplierName),
  }))
}

function parseUseCase(row: Record<string, unknown>): P4UseCase {
  return {
    id: stringValue(row.id),
    title: stringValue(row.title),
    businessPurpose: stringValue(row.businessPurpose),
    expectedOutcome: nullableString(row.expectedOutcome),
    businessUnitId: nullableString(row.businessUnitId),
    businessUnitName: nullableString(row.businessUnitName),
    businessOwnerMembershipId: nullableString(row.businessOwnerMembershipId),
    businessOwnerName: nullableString(row.businessOwnerName),
    technicalOwnerMembershipId: nullableString(row.technicalOwnerMembershipId),
    technicalOwnerName: nullableString(row.technicalOwnerName),
    operationalState: stringValue(row.operationalState, 'proposed') as P4UseCase['operationalState'],
    governanceStatus: stringValue(row.governanceStatus, 'Assessment required'),
    source: stringValue(row.source),
    createdAt: stringValue(row.createdAt),
    products: records(row.products).map((item) => ({ id: stringValue(item.id), name: stringValue(item.name), supplierName: nullableString(item.supplierName) })),
    objectives: records(row.objectives).map((item) => ({ id: stringValue(item.id), title: stringValue(item.title) })),
    initiatives: records(row.initiatives).map((item) => ({ id: stringValue(item.id), name: stringValue(item.name), stage: nullableString(item.stage) ?? undefined })),
  }
}

export async function listP4UseCases(input: {
  organisationId: string
  search?: string
  businessUnitId?: string | null
  ownerMembershipId?: string | null
  operationalState?: string | null
  productId?: string | null
  objectiveId?: string | null
  initiativeId?: string | null
}): Promise<P4UseCase[]> {
  const data = await invoke('list_p4_use_cases', {
    p_organisation_id: input.organisationId,
    p_search: input.search || null,
    p_business_unit_id: input.businessUnitId || null,
    p_owner_membership_id: input.ownerMembershipId || null,
    p_operational_state: input.operationalState || null,
    p_product_id: input.productId || null,
    p_objective_id: input.objectiveId || null,
    p_initiative_id: input.initiativeId || null,
  })
  return records(data).map(parseUseCase)
}

export async function getP4UseCase(useCaseId: string): Promise<P4UseCaseDetail> {
  const data = await invoke('get_p4_use_case', { p_ai_use_case_id: useCaseId })
  if (!isRecord(data)) throw new Error('AI Use Case response was invalid.')
  const parsed = parseUseCase(data)
  return {
    ...parsed,
    organisationId: stringValue(data.organisationId),
    updatedAt: stringValue(data.updatedAt),
    candidateSourceId: nullableString(data.candidateSourceId),
    activity: records(data.activity).map((item) => ({
      id: stringValue(item.id),
      eventType: stringValue(item.eventType),
      occurredAt: stringValue(item.occurredAt),
      eventData: isRecord(item.eventData) ? item.eventData : {},
    })),
  }
}

export async function createP4UseCase(input: {
  organisationId: string
  title: string
  businessPurpose: string
  expectedOutcome?: string | null
  businessUnitId?: string | null
  businessOwnerMembershipId?: string | null
  technicalOwnerMembershipId?: string | null
  operationalState: P4UseCase['operationalState']
}) {
  return stringValue(await invoke('create_p4_use_case', {
    p_organisation_id: input.organisationId,
    p_title: input.title,
    p_business_purpose: input.businessPurpose,
    p_business_unit_id: input.businessUnitId || null,
    p_business_owner_membership_id: input.businessOwnerMembershipId || null,
    p_technical_owner_membership_id: input.technicalOwnerMembershipId || null,
    p_operational_state: input.operationalState,
    p_expected_outcome: input.expectedOutcome || null,
  }))
}

export async function updateP4UseCase(input: Omit<Parameters<typeof createP4UseCase>[0], 'organisationId'> & { useCaseId: string }) {
  await invoke('update_p4_use_case', {
    p_ai_use_case_id: input.useCaseId,
    p_title: input.title,
    p_business_purpose: input.businessPurpose,
    p_expected_outcome: input.expectedOutcome || null,
    p_business_unit_id: input.businessUnitId || null,
    p_business_owner_membership_id: input.businessOwnerMembershipId || null,
    p_technical_owner_membership_id: input.technicalOwnerMembershipId || null,
    p_operational_state: input.operationalState,
  })
}

export async function setP4UseCaseProducts(useCaseId: string, productIds: string[]) {
  await invoke('set_p4_use_case_products', { p_ai_use_case_id: useCaseId, p_product_ids: productIds })
}

export async function setP4UseCaseObjectives(useCaseId: string, objectiveIds: string[]) {
  await invoke('set_p4_use_case_objectives', { p_ai_use_case_id: useCaseId, p_objective_ids: objectiveIds })
}

export async function setP4UseCaseInitiatives(useCaseId: string, initiativeIds: string[]) {
  await invoke('set_p4_use_case_initiatives', { p_ai_use_case_id: useCaseId, p_initiative_ids: initiativeIds })
}


export type Dec039StrategyTheme =
  | 'customers_community' | 'operations_productivity' | 'growth_innovation'
  | 'financial_sustainability' | 'people_capability' | 'risk_resilience'
  | 'data_decisions' | 'sustainability_impact' | 'other_mixed'

export type Dec040PrioritySource =
  | 'organisation_strategy' | 'business_unit_strategy' | 'leadership_direction'
  | 'customer_user_need' | 'risk_regulatory_compliance' | 'operational_need'
  | 'other' | 'not_recorded'

export type Dec039LinkedInitiative = { id: string; name: string; stage: string; expectedOutcome: string | null }
export type Dec039LinkedAiUse = { id: string; title: string; operationalState: string; governanceStatus: string }
export type Dec039Contribution = {
  id: string
  title: string
  description: string | null
  intendedOutcome: string | null
  successMeasure: string | null
  target: string | null
  initiatives: Dec039LinkedInitiative[]
  aiUses: Dec039LinkedAiUse[]
  outcomeMeasured: boolean
}

export type Dec039LandscapePriority = {
  id: string
  title: string
  description: string | null
  strategyTheme: Dec039StrategyTheme
  aiRelevance: 'high' | 'medium' | 'low' | 'unknown'
  contributions: Dec039Contribution[]
}

export type Dec039Landscape = {
  organisationStrategyVersion: { id: string; name: string; source: string; versionNo: number; effectiveFrom: string | null } | null
  aiAlignmentVersion: { id: string; name: string; versionNo: number; organisationStrategyVersionId: string; basisSource: string } | null
  priorities: Dec039LandscapePriority[]
  gapSignals: { opportunityNoContribution: number; contributionNoActivity: number; outcomeNotMeasured: number; aiUseNoAlignment: number; initiativeNoAlignment: number }
}

export async function getDec039StrategyLandscape(organisationId: string): Promise<Dec039Landscape> {
  const row = await invoke('get_dec039_strategy_landscape', { p_organisation_id: organisationId })
  const data = isRecord(row) ? row : {}
  const basis = isRecord(data.organisationStrategyVersion) ? data.organisationStrategyVersion : null
  const alignment = isRecord(data.aiAlignmentVersion) ? data.aiAlignmentVersion : null
  return {
    organisationStrategyVersion: basis ? {
      id: stringValue(basis.id), name: stringValue(basis.name), source: stringValue(basis.source),
      versionNo: numberValue(basis.versionNo), effectiveFrom: nullableString(basis.effectiveFrom),
    } : null,
    aiAlignmentVersion: alignment ? {
      id: stringValue(alignment.id), name: stringValue(alignment.name), versionNo: numberValue(alignment.versionNo),
      organisationStrategyVersionId: stringValue(alignment.organisationStrategyVersionId), basisSource: stringValue(alignment.basisSource),
    } : null,
    gapSignals: (() => { const gaps = isRecord(data.gapSignals) ? data.gapSignals : {}; return {
      opportunityNoContribution: numberValue(gaps.opportunityNoContribution), contributionNoActivity: numberValue(gaps.contributionNoActivity),
      outcomeNotMeasured: numberValue(gaps.outcomeNotMeasured), aiUseNoAlignment: numberValue(gaps.aiUseNoAlignment),
      initiativeNoAlignment: numberValue(gaps.initiativeNoAlignment),
    } })(),
    priorities: records(data.priorities).map((priority) => ({
      id: stringValue(priority.id),
      title: stringValue(priority.title),
      description: nullableString(priority.description),
      strategyTheme: stringValue(priority.strategyTheme, 'other_mixed') as Dec039StrategyTheme,
      aiRelevance: stringValue(priority.aiRelevance, 'unknown') as Dec039LandscapePriority['aiRelevance'],
      contributions: records(priority.contributions).map((contribution) => ({
        id: stringValue(contribution.id), title: stringValue(contribution.title),
        description: nullableString(contribution.description),
        intendedOutcome: nullableString(contribution.intendedOutcome),
        successMeasure: nullableString(contribution.successMeasure),
        target: nullableString(contribution.target),
        initiatives: records(contribution.initiatives).map((initiative) => ({ id: stringValue(initiative.id), name: stringValue(initiative.name), stage: stringValue(initiative.stage), expectedOutcome: nullableString(initiative.expectedOutcome) })),
        aiUses: records(contribution.aiUses).map((useCase) => ({ id: stringValue(useCase.id), title: stringValue(useCase.title), operationalState: stringValue(useCase.operationalState), governanceStatus: stringValue(useCase.governanceStatus) })),
        outcomeMeasured: booleanValue(contribution.outcomeMeasured),
      })),
    })),
  }
}

export async function saveDec039OrganisationStrategy(input: {
  organisationId: string
  name: string
  source: 'customer_existing' | 'swell_established'
  periodStart?: string | null
  periodEnd?: string | null
  priorities: { title: string; description?: string | null; displayOrder: number; strategyTheme: Dec039StrategyTheme; aiRelevance: 'high' | 'medium' | 'low' | 'unknown'; prioritySource: Dec040PrioritySource; prioritySourceOtherDescription?: string | null }[]
}) {
  const value = await invoke('save_dec039_organisation_strategy', {
    p_organisation_id: input.organisationId, p_name: input.name, p_source: input.source, p_priorities: input.priorities,
    p_period_start: input.periodStart || null, p_period_end: input.periodEnd || null,
  })
  return isRecord(value) ? { organisationStrategyVersionId: stringValue(value.organisationStrategyVersionId), aiAlignmentVersionId: stringValue(value.aiAlignmentVersionId) } : null
}

export async function createDec039AiContribution(input: {
  organisationStrategyPriorityId: string
  title: string
  description?: string | null
  intendedOutcome?: string | null
  successMeasure?: string | null
  target?: string | null
}) {
  return stringValue(await invoke('create_dec039_ai_contribution', {
    p_organisation_strategy_priority_id: input.organisationStrategyPriorityId,
    p_title: input.title, p_description: input.description || null,
    p_intended_outcome: input.intendedOutcome || null, p_success_measure: input.successMeasure || null, p_target: input.target || null,
  }))
}

export type SimpleStrategyAiUse = {
  id: string
  title: string
  operationalState: P4UseCase['operationalState']
  governanceStatus: string
}

export type SimpleStrategyInitiative = {
  id: string
  name: string
  description: string | null
  stage: P4InitiativeStage
  ownerMembershipId: string | null
  startYear: number | null
  startQuarter: number | null
  endYear: number | null
  endQuarter: number | null
  ongoing: boolean
  maturityState: 'no_ai_uses_linked' | 'ai_uses_linked' | 'active_ai_uses' | 'complete'
  linkedAiUseCount: number
  activeAiUseCount: number
  priorities: { id: string; title: string }[]
  aiUses: SimpleStrategyAiUse[]
}

export type SimpleStrategyPriority = {
  id: string
  title: string
  description: string | null
  focusYears: number[]
  initiatives: SimpleStrategyInitiative[]
}

export type SimpleStrategy = {
  strategy: { id: string; name: string; startYear: number | null; endYear: number | null } | null
  priorities: SimpleStrategyPriority[]
  initiatives: SimpleStrategyInitiative[]
  aiUses: SimpleStrategyAiUse[]
  summary: { totalPriorities: number; totalInitiatives: number; totalAiUses: number; alignedInitiatives: number; linkedAiUses: number }
}

export async function getSimpleStrategy(organisationId: string): Promise<SimpleStrategy> {
  const value = await invoke('get_simple_strategy', { p_organisation_id: organisationId })
  const data = isRecord(value) ? value : {}
  const strategy = isRecord(data.strategy) ? data.strategy : null
  const parseAiUse = (row: Record<string, unknown>): SimpleStrategyAiUse => ({
    id: stringValue(row.id),
    title: stringValue(row.title),
    operationalState: stringValue(row.operationalState, 'proposed') as P4UseCase['operationalState'],
    governanceStatus: stringValue(row.governanceStatus, 'Assessment required'),
  })
  const parseInitiative = (row: Record<string, unknown>): SimpleStrategyInitiative => ({
    id: stringValue(row.id),
    name: stringValue(row.name),
    description: nullableString(row.description),
    stage: stringValue(row.stage, 'planned') as P4InitiativeStage,
    ownerMembershipId: nullableString(row.ownerMembershipId),
    startYear: row.startYear === null || row.startYear === undefined ? null : numberValue(row.startYear),
    startQuarter: row.startQuarter === null || row.startQuarter === undefined ? null : numberValue(row.startQuarter),
    endYear: row.endYear === null || row.endYear === undefined ? null : numberValue(row.endYear),
    endQuarter: row.endQuarter === null || row.endQuarter === undefined ? null : numberValue(row.endQuarter),
    ongoing: booleanValue(row.ongoing),
    maturityState: stringValue(row.maturityState, 'no_ai_uses_linked') as SimpleStrategyInitiative['maturityState'],
    linkedAiUseCount: numberValue(row.linkedAiUseCount),
    activeAiUseCount: numberValue(row.activeAiUseCount),
    priorities: records(row.priorities).map((priority) => ({ id: stringValue(priority.id), title: stringValue(priority.title) })),
    aiUses: records(row.aiUses).map(parseAiUse),
  })
  const summary = isRecord(data.summary) ? data.summary : {}
  return {
    strategy: strategy ? {
      id: stringValue(strategy.id),
      name: stringValue(strategy.name),
      startYear: strategy.startYear === null || strategy.startYear === undefined ? null : numberValue(strategy.startYear),
      endYear: strategy.endYear === null || strategy.endYear === undefined ? null : numberValue(strategy.endYear),
    } : null,
    priorities: records(data.priorities).map((priority) => ({
      id: stringValue(priority.id),
      title: stringValue(priority.title),
      description: nullableString(priority.description),
      focusYears: numberArray(priority.focusYears),
      initiatives: records(priority.initiatives).map(parseInitiative),
    })),
    initiatives: records(data.initiatives).map(parseInitiative),
    aiUses: records(data.aiUses).map(parseAiUse),
    summary: {
      totalPriorities: numberValue(summary.totalPriorities),
      totalInitiatives: numberValue(summary.totalInitiatives),
      totalAiUses: numberValue(summary.totalAiUses),
      alignedInitiatives: numberValue(summary.alignedInitiatives),
      linkedAiUses: numberValue(summary.linkedAiUses),
    },
  }
}

export async function saveSimpleOrganisationStrategy(input: {
  organisationId: string
  name: string
  startYear: number
  endYear: number
}) {
  await invoke('save_simple_organisation_strategy', {
    p_organisation_id: input.organisationId,
    p_name: input.name,
    p_start_year: input.startYear,
    p_end_year: input.endYear,
  })
}

export async function saveSimpleStrategyPriority(input: {
  organisationId: string
  priorityId?: string | null
  title: string
  description?: string | null
  startYear?: number | null
  endYear?: number | null
}) {
  return stringValue(await invoke('save_simple_strategy_priority', {
    p_organisation_id: input.organisationId,
    p_priority_id: input.priorityId || null,
    p_title: input.title,
    p_description: input.description || null,
    p_start_year: input.startYear ?? null,
    p_end_year: input.endYear ?? null,
  }))
}

export async function setSimpleStrategyPriorityAiUses(priorityId: string, aiUseIds: string[]) {
  await invoke('set_simple_strategy_priority_ai_uses', {
    p_priority_id: priorityId,
    p_ai_use_ids: aiUseIds,
  })
}

export async function saveSimpleAiInitiative(input: {
  organisationId: string
  initiativeId?: string | null
  name: string
  description?: string | null
  startYear: number
  startQuarter: number
  endYear: number | null
  endQuarter: number | null
}) {
  return stringValue(await invoke('save_simple_ai_initiative', {
    p_organisation_id: input.organisationId,
    p_initiative_id: input.initiativeId || null,
    p_name: input.name,
    p_description: input.description || null,
    p_start_year: input.startYear,
    p_start_quarter: input.startQuarter,
    p_end_year: input.endYear,
    p_end_quarter: input.endQuarter,
  }))
}

export async function createSimpleAiInitiativeForPriority(input: {
  organisationId: string
  priorityId: string
  name: string
  description?: string | null
  startYear: number
  startQuarter: number
  endYear: number | null
  endQuarter: number | null
}) {
  return stringValue(await invoke('create_simple_ai_initiative_for_priority', {
    p_organisation_id: input.organisationId,
    p_priority_id: input.priorityId,
    p_name: input.name,
    p_description: input.description || null,
    p_start_year: input.startYear,
    p_start_quarter: input.startQuarter,
    p_end_year: input.endYear,
    p_end_quarter: input.endQuarter,
  }))
}

export async function setSimpleStrategyPriorityInitiatives(priorityId: string, initiativeIds: string[]) {
  await invoke('set_simple_strategy_priority_initiatives', {
    p_priority_id: priorityId,
    p_initiative_ids: initiativeIds,
  })
}

export async function setSimpleInitiativePriorities(initiativeId: string, priorityIds: string[]) {
  await invoke('set_simple_initiative_priorities', {
    p_initiative_id: initiativeId,
    p_priority_ids: priorityIds,
  })
}

export async function setSimpleInitiativeAiUses(initiativeId: string, aiUseIds: string[]) {
  await invoke('set_simple_initiative_ai_uses', {
    p_initiative_id: initiativeId,
    p_ai_use_ids: aiUseIds,
  })
}

export async function setSimpleAiUseInitiatives(aiUseCaseId: string, initiativeIds: string[]) {
  await invoke('set_simple_ai_use_initiatives', {
    p_ai_use_case_id: aiUseCaseId,
    p_initiative_ids: initiativeIds,
  })
}

export async function setSimpleAiUseStrategyPriorities(aiUseCaseId: string, priorityIds: string[]) {
  await invoke('set_simple_ai_use_strategy_priorities', {
    p_ai_use_case_id: aiUseCaseId,
    p_priority_ids: priorityIds,
  })
}

export async function setSimpleStrategyPriorityYear(priorityId: string, year: number, active: boolean) {
  await invoke('set_simple_strategy_priority_year', {
    p_priority_id: priorityId,
    p_year: year,
    p_active: active,
  })
}
